import React, { useEffect, useState } from "react";
import DefaultLayout from "../../layouts/DefaultLayout";
import { useLocation, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import SignUpForm from "./SignUp/SignUpForm";
import ErrorMessage from "../common/ErrorMessage";
import BackdropLoader from "../common/BackdropLoader";
import { validateReferralLink } from "../../api/client";
import { APPLICATION_SOURCE } from "@booyaltd/core";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `sx`.
 * See docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS ESCALATION CLASS.** Referral-link landing page — validates
 * the code with the API before letting the signup funnel start. Styling-only:
 * `validateReferralLink`, the error/validated state machine and the
 * `SignUpForm` props are unchanged. `createStyles` was a v4 type helper with
 * no v5+ equivalent and no runtime effect; it disappears with `makeStyles`.
 */

/**
 * was: useStyles().root, merged with the three layout props the same
 * `<Grid container>` carried — `justify="center"`, `alignItems="center"` and
 * `direction="column"`.
 *
 * None of the three survives on v9's Grid. `justify`/`alignItems` were removed
 * outright (guide §2), and `direction` now accepts only `"row"`/`"row-reverse"`
 * — v9 dropped `column` support entirely, because the Grid's width maths
 * assumes a row axis (upstream steers vertical layouts at `Stack`). This
 * element is a real flex container, so all three genuinely rendered in v4.
 * Writing `flexDirection: "column"` in `sx` emits exactly the declaration v4's
 * `.MuiGrid-direction-xs-column` did, without depending on a prop that is now
 * unsupported. The only in-flow child here is the signup form (or the error
 * message), so this is a one-child flex container either way.
 */
const rootSx: SxProps<Theme> = {
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "background.default",
  // margin: theme.spacing(2),
  // padding: theme.spacing(2)
};

const Referral = () => {
  const { code } = useParams<{ code?: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setError(undefined);
    setValidated(false);

    if (!code) {
      setError("Invalid referral link");
      return;
    }

    setLoading(true);
    validateReferralLink(code)
      .then((result) => {
        if (result === true) {
          setValidated(true);
        } else {
          setError(result);
        }
      })
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <DefaultLayout showSignIn={false} showTry={false}>
      <Grid
        container
        // spacing={2}
        sx={rootSx}
      >
        {loading && <BackdropLoader />}
        {!loading && (
          <>
            {!validated && error !== undefined && (
              <ErrorMessage
                title="Invalid Referral Link"
                message={error}
                linkHome={true}
              />
            )}
            {validated && code && (
              <SignUpForm
                route={undefined}
                source={APPLICATION_SOURCE.REFERRAL}
                code={code}
                page={location.pathname}
              />
            )}
          </>
        )}
      </Grid>
    </DefaultLayout>
  );
};

export default Referral;
